import type {
  PreCheckInByDayResponse,
  PreCheckOutByDayResponse,
} from "@/types/analytics-overview";
import type { FunnelStep } from "./FlowCompletionCard";

type GuestPathTrackMetric = {
  label: string;
  value: number;
  color: string;
};

const TRACK_COLORS = ["#D4D4D8", "#C4B5FD", "#A78BFA", "#8B5CF6", "#7C3AED"];

const PRE_CHECK_IN_STEP_CONFIG: Array<{
  key: keyof PreCheckInByDayResponse["totals"];
  title: string;
  step: string;
}> = [
  { key: "start", title: "Start Pre-Check-In", step: "Start" },
  { key: "contactInformation", title: "Contact information", step: "Step 1" },
  {
    key: "foundBookingInfo",
    title: "We've found your Booking Info",
    step: "Step 2",
  },
  { key: "upgradeRoom", title: "Upgrade room", step: "Step 3" },
  {
    key: "selectDocumentType",
    title: "Please select the appropriate document type",
    step: "Step 4",
  },
  { key: "additionalInfo", title: "Additional information", step: "Step 5" },
  { key: "paymentForStay", title: "Payment for Stay", step: "Step 6" },
  { key: "additionalDetails", title: "Additional details", step: "Step 7" },
  {
    key: "planOut",
    title: "Plan out all your services and activities",
    step: "Step 8",
  },
  { key: "completed", title: "Pre-Check-in is completed", step: "Completed" },
];

const PRE_CHECK_OUT_STEP_CONFIG: Array<{
  key: keyof PreCheckOutByDayResponse["totals"];
  title: string;
  step: string;
}> = [
  { key: "start", title: "Make sure the info is correct", step: "Start" },
  {
    key: "additionalServices",
    title: "Additional services before departure",
    step: "Step 1",
  },
  { key: "billSummary", title: "Bill Summary", step: "Step 2" },
  { key: "review", title: "Review", step: "Step 3" },
  { key: "completed", title: "Pre-Check-Out is completed", step: "Step 4" },
];

function toSafeNumber(value: number | null | undefined): number {
  return Number.isFinite(value) ? Math.max(0, Number(value)) : 0;
}

export function toCompletionRate(completed: number, started: number): number {
  if (started <= 0) {
    return 0;
  }

  const rate = (Math.max(0, completed) / started) * 100;

  return Math.min(100, Math.round(rate * 10) / 10);
}

function mapFunnelSteps<
  TTotals extends Record<string, number>,
  TKey extends keyof TTotals,
>(
  totals: TTotals | undefined,
  config: Array<{ key: TKey; title: string; step: string }>,
): FunnelStep[] {
  let previousCompleted = 0;

  return config.map((item, index) => {
    const recordedCompleted = toSafeNumber(totals?.[item.key]);
    const total =
      index === 0 ? recordedCompleted : previousCompleted || recordedCompleted;
    const completed = Math.min(recordedCompleted, total);

    previousCompleted = completed;

    return {
      title: item.title,
      step: item.step,
      completed,
      total,
    };
  });
}

export function mapPreCheckInSteps(
  response?: PreCheckInByDayResponse,
): FunnelStep[] {
  return mapFunnelSteps(response?.totals, PRE_CHECK_IN_STEP_CONFIG);
}

export function mapPreCheckOutSteps(
  response?: PreCheckOutByDayResponse,
): FunnelStep[] {
  return mapFunnelSteps(response?.totals, PRE_CHECK_OUT_STEP_CONFIG);
}

export function mapPreCheckInTrackMetrics(
  response?: PreCheckInByDayResponse,
): GuestPathTrackMetric[] {
  const totals = response?.totals;

  return [
    {
      label: "Email sent",
      value: toSafeNumber(totals?.emailSent),
      color: TRACK_COLORS[0],
    },
    {
      label: "Email delivered",
      value: toSafeNumber(totals?.emailDelivered),
      color: TRACK_COLORS[1],
    },
    {
      label: "Email opened",
      value: toSafeNumber(totals?.emailOpened),
      color: TRACK_COLORS[2],
    },
    {
      label: "Pre-check-in started",
      value: toSafeNumber(totals?.start),
      color: TRACK_COLORS[3],
    },
    {
      label: "Pre-check-in completed",
      value: toSafeNumber(totals?.completed),
      color: TRACK_COLORS[4],
    },
  ];
}

export function mapPreCheckOutTrackMetrics(
  response?: PreCheckOutByDayResponse,
): GuestPathTrackMetric[] {
  const totals = response?.totals;

  return [
    {
      label: "Email sent",
      value: toSafeNumber(totals?.emailSent),
      color: TRACK_COLORS[0],
    },
    {
      label: "Email delivered",
      value: toSafeNumber(totals?.emailDelivered),
      color: TRACK_COLORS[1],
    },
    {
      label: "Email opened",
      value: toSafeNumber(totals?.emailOpened),
      color: TRACK_COLORS[2],
    },
    {
      label: "Pre-check-out started",
      value: toSafeNumber(totals?.start),
      color: TRACK_COLORS[3],
    },
    {
      label: "Pre-check-out completed",
      value: toSafeNumber(totals?.completed),
      color: TRACK_COLORS[4],
    },
  ];
}

export function toRingValues(metrics: GuestPathTrackMetric[]): number[] {
  const baseline = metrics[0]?.value ?? 0;

  if (baseline <= 0) {
    return metrics.map(() => 0);
  }

  return metrics.map((metric) =>
    Math.max(0, Math.min(100, Math.round((metric.value / baseline) * 100))),
  );
}
